export const LINK_TELEMETRY_CATEGORIES = [
  'bandwidth',
  'utilization',
  'errors',
  'packets',
  'status',
  'temperature',
  'optics',
  'generic'
] as const;

export type LinkTelemetryCategory = (typeof LINK_TELEMETRY_CATEGORIES)[number];
export type LinkTelemetryEndpoint = 'a' | 'z';
export type LinkTelemetryDirection = 'rx' | 'tx' | 'none';
export type LinkTelemetryAvailability =
  | 'available'
  | 'unsupported'
  | 'unavailable'
  | 'no_samples_in_range'
  | 'stale'
  | 'disabled'
  | 'failed';
export type LinkTelemetryViewState =
  | LinkTelemetryAvailability
  | 'loading'
  | 'partial';

export type LinkTelemetryPoint = {
  ts: number;
  value: number;
};

export type LinkTelemetryThresholds = {
  warning_low?: number;
  warning_high?: number;
  critical_low?: number;
  critical_high?: number;
};

export type LinkTelemetryAvailabilityDetail = {
  state: LinkTelemetryAvailability;
  diagnostic?: string;
  observed_at?: string;
  expected_interval_seconds?: number;
};

export type LinkTelemetryMetric = {
  metric_id?: string;
  category?: string;
  endpoint?: string;
  direction?: string;
  scope?: string;
  unit?: string;
  component_id?: string;
  sensor_id?: string;
  label?: string;
  role?: string;
  lane?: string;
  health?: string;
  warning_min?: number;
  warning_max?: number;
  critical_min?: number;
  critical_max?: number;
  observed_at?: string;
  availability?: LinkTelemetryAvailability | LinkTelemetryAvailabilityDetail;
  diagnostic?: string;
  points?: LinkTelemetryPoint[];
  // Rollout-only fields. They are display fallbacks, never classification inputs.
  name?: string;
  thresholds?: LinkTelemetryThresholds;
};

export type LinkTelemetryGroup = {
  category: string;
  availability: LinkTelemetryAvailability;
  diagnostic?: string;
};

export type LinkTelemetrySide = {
  endpoint?: LinkTelemetryEndpoint;
  host?: string;
  interface_name?: string;
  interface_ifindex?: number;
  availability?: LinkTelemetryAvailability | LinkTelemetryAvailabilityDetail;
  groups?: LinkTelemetryGroup[];
  // Rollout-only compatibility shape.
  category_states?: Partial<Record<LinkTelemetryCategory, LinkTelemetryAvailabilityDetail>>;
  metrics?: LinkTelemetryMetric[];
};

export type LinkTelemetrySeriesResponse = {
  server_time?: string;
  telemetry_updated_at?: string;
  enabled?: boolean;
  link_id?: string;
  range_seconds?: number;
  source?: LinkTelemetrySide;
  target?: LinkTelemetrySide;
  error?: string;
  diagnostic?: string;
};

export type LinkTelemetryMetricViewModel = {
  metricId: string;
  category: LinkTelemetryCategory;
  endpoint: LinkTelemetryEndpoint;
  direction: LinkTelemetryDirection;
  scope: string;
  unit: string;
  componentId?: string;
  sensorId?: string;
  label: string;
  role?: string;
  lane?: string;
  health?: string;
  warningMin?: number;
  warningMax?: number;
  criticalMin?: number;
  criticalMax?: number;
  observedAt?: string;
  state: LinkTelemetryAvailability;
  diagnostic?: string;
  points: LinkTelemetryPoint[];
  currentValue?: number;
};

export type LinkTelemetryEndpointViewModel = {
  endpoint: LinkTelemetryEndpoint;
  state: LinkTelemetryAvailability;
  diagnostic?: string;
  metrics: LinkTelemetryMetricViewModel[];
};

export type LinkTelemetryUnitGroupViewModel = {
  unit: string;
  metrics: LinkTelemetryMetricViewModel[];
};

export type LinkTelemetryBucketViewModel = {
  category: LinkTelemetryCategory;
  state: LinkTelemetryViewState;
  endpoints: Record<LinkTelemetryEndpoint, LinkTelemetryEndpointViewModel>;
  unitGroups: LinkTelemetryUnitGroupViewModel[];
  hasPartialData: boolean;
};

export type LinkDetailTelemetryViewModel = {
  linkId?: string;
  state: LinkTelemetryViewState;
  serverTime?: string;
  telemetryUpdatedAt?: string;
  diagnostic?: string;
  buckets: LinkTelemetryBucketViewModel[];
};

const KNOWN_CATEGORIES = new Set<string>(LINK_TELEMETRY_CATEGORIES);
const AVAILABILITY_STATES = new Set<LinkTelemetryAvailability>([
  'available',
  'unsupported',
  'unavailable',
  'no_samples_in_range',
  'stale',
  'disabled',
  'failed'
]);

const safeText = (value: unknown, limit = 320) =>
  typeof value === 'string' ? value.trim().slice(0, limit) : '';

const parseTimestamp = (value: unknown) => {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

const normalizeCategory = (value: unknown): LinkTelemetryCategory => {
  const normalized = safeText(value, 64).toLowerCase();
  return KNOWN_CATEGORIES.has(normalized)
    ? (normalized as LinkTelemetryCategory)
    : 'generic';
};

const normalizeEndpoint = (
  value: unknown,
  fallback: LinkTelemetryEndpoint
): LinkTelemetryEndpoint => {
  const normalized = safeText(value, 8).toLowerCase();
  return normalized === 'a' || normalized === 'z' ? normalized : fallback;
};

const normalizeDirection = (value: unknown): LinkTelemetryDirection => {
  const normalized = safeText(value, 16).toLowerCase();
  return normalized === 'rx' || normalized === 'tx' ? normalized : 'none';
};

const availabilityDetail = (
  availability:
    | LinkTelemetryMetric['availability']
    | LinkTelemetrySide['availability']
): LinkTelemetryAvailabilityDetail | undefined => {
  if (typeof availability === 'string') {
    return AVAILABILITY_STATES.has(availability)
      ? { state: availability }
      : undefined;
  }
  if (
    availability &&
    typeof availability === 'object' &&
    AVAILABILITY_STATES.has(availability.state)
  ) {
    return availability;
  }
  return undefined;
};

const freshnessState = (
  detail: LinkTelemetryAvailabilityDetail | undefined,
  observedAt: string | undefined,
  nowMs: number
) => {
  if (detail?.state && detail.state !== 'available') return detail.state;
  const expectedSeconds = detail?.expected_interval_seconds;
  const observedMs = parseTimestamp(observedAt ?? detail?.observed_at);
  if (
    observedMs !== undefined &&
    Number.isFinite(expectedSeconds) &&
    Number(expectedSeconds) > 0 &&
    nowMs - observedMs > Number(expectedSeconds) * 2 * 1000
  ) {
    return 'stale' as const;
  }
  return detail?.state;
};

const latestPoint = (points: LinkTelemetryPoint[]) =>
  points.reduce<LinkTelemetryPoint | undefined>(
    (latest, point) => (!latest || point.ts > latest.ts ? point : latest),
    undefined
  );

const normalizeMetric = (
  metric: LinkTelemetryMetric,
  fallbackEndpoint: LinkTelemetryEndpoint,
  sideAvailability: LinkTelemetryAvailabilityDetail | undefined,
  nowMs: number,
  index: number,
  retainedDiagnostic?: string
): LinkTelemetryMetricViewModel => {
  const category = normalizeCategory(metric.category);
  const endpoint = normalizeEndpoint(metric.endpoint, fallbackEndpoint);
  const points = Array.isArray(metric.points)
    ? metric.points.filter(
        point =>
          point &&
          Number.isFinite(point.ts) &&
          Number.isFinite(point.value)
      )
    : [];
  const detail = availabilityDetail(metric.availability) ?? sideAvailability;
  const observedAt = safeText(metric.observed_at || detail?.observed_at) || undefined;
  const derivedFreshness = freshnessState(detail, observedAt, nowMs);
  let state =
    derivedFreshness ??
    (points.length > 0 ? 'available' : 'no_samples_in_range');
  if (retainedDiagnostic && state === 'available' && points.length > 0) {
    state = 'stale';
  }
  const metricId =
    safeText(metric.metric_id, 160) ||
    `${endpoint}:${category}:${index}`;
  const label =
    safeText(metric.label, 160) ||
    safeText(metric.name, 160) ||
    metricId;

  return {
    metricId,
    category,
    endpoint,
    direction: normalizeDirection(metric.direction),
    scope: safeText(metric.scope, 64) || 'unknown',
    unit: safeText(metric.unit, 32),
    componentId: safeText(metric.component_id, 160) || undefined,
    sensorId: safeText(metric.sensor_id, 160) || undefined,
    label,
    role: safeText(metric.role, 64) || undefined,
    lane: safeText(metric.lane, 64) || undefined,
    health: safeText(metric.health, 32) || undefined,
    warningMin:
      Number.isFinite(metric.warning_min)
        ? Number(metric.warning_min)
        : metric.thresholds?.warning_low,
    warningMax:
      Number.isFinite(metric.warning_max)
        ? Number(metric.warning_max)
        : metric.thresholds?.warning_high,
    criticalMin:
      Number.isFinite(metric.critical_min)
        ? Number(metric.critical_min)
        : metric.thresholds?.critical_low,
    criticalMax:
      Number.isFinite(metric.critical_max)
        ? Number(metric.critical_max)
        : metric.thresholds?.critical_high,
    observedAt,
    state,
    diagnostic:
      retainedDiagnostic ||
      safeText(metric.diagnostic) ||
      safeText(detail?.diagnostic) ||
      undefined,
    points,
    currentValue: latestPoint(points)?.value
  };
};

const endpointState = (
  metrics: LinkTelemetryMetricViewModel[],
  fallback: LinkTelemetryAvailabilityDetail | undefined
): LinkTelemetryAvailability => {
  if (fallback?.state && fallback.state !== 'available') {
    return fallback.state;
  }
  if (metrics.some(metric => metric.state === 'available')) return 'available';
  if (metrics.some(metric => metric.state === 'stale')) return 'stale';
  if (metrics.length === 0 && fallback?.state === 'available') {
    return 'no_samples_in_range';
  }
  return (
    fallback?.state ??
    metrics[0]?.state ??
    'no_samples_in_range'
  );
};

const groupAvailability = (
  side: LinkTelemetrySide | undefined,
  category: LinkTelemetryCategory
): LinkTelemetryAvailabilityDetail | undefined => {
  const group = side?.groups?.find(
    candidate => normalizeCategory(candidate.category) === category
  );
  if (group && AVAILABILITY_STATES.has(group.availability)) {
    return {
      state: group.availability,
      diagnostic: safeText(group.diagnostic) || undefined
    };
  }
  return side?.category_states?.[category];
};

const bucketState = (
  a: LinkTelemetryEndpointViewModel,
  z: LinkTelemetryEndpointViewModel
): LinkTelemetryViewState => {
  const available = (state: LinkTelemetryAvailability) =>
    state === 'available' || state === 'stale';
  if (available(a.state) && available(z.state)) {
    return a.state === 'stale' || z.state === 'stale'
      ? 'stale'
      : 'available';
  }
  if (available(a.state) || available(z.state)) return 'partial';
  if (a.state === z.state) return a.state;
  if (a.state === 'failed' || z.state === 'failed') return 'failed';
  if (a.state === 'disabled' || z.state === 'disabled') return 'disabled';
  if (a.state === 'unavailable' || z.state === 'unavailable') return 'unavailable';
  if (a.state === 'unsupported' && z.state === 'unsupported') return 'unsupported';
  return 'no_samples_in_range';
};

const buildEndpoint = (
  endpoint: LinkTelemetryEndpoint,
  metrics: LinkTelemetryMetricViewModel[],
  fallback: LinkTelemetryAvailabilityDetail | undefined
): LinkTelemetryEndpointViewModel => ({
  endpoint,
  state: endpointState(metrics, fallback),
  diagnostic: safeText(fallback?.diagnostic) || undefined,
  metrics
});

export const buildLinkDetailTelemetryViewModel = (
  response: LinkTelemetrySeriesResponse | null,
  options: {
    loading?: boolean;
    nowMs?: number;
    error?: string | null;
    disabled?: boolean;
  } = {}
): LinkDetailTelemetryViewModel => {
  const nowMs = options.nowMs ?? Date.now();
  const optionFailure = safeText(options.error);
  const responseFailure = optionFailure || safeText(response?.error);
  const responseDiagnostic =
    responseFailure || safeText(response?.diagnostic) || undefined;
  const hasUsablePoints = [response?.source, response?.target].some(side =>
    side?.metrics?.some(metric => (metric.points?.length ?? 0) > 0)
  );
  const retainedDiagnostic =
    hasUsablePoints && (responseFailure || options.disabled)
      ? responseFailure || responseDiagnostic || 'Telemetry refresh is disabled.'
      : undefined;
  const forcedState: LinkTelemetryAvailability | undefined =
    responseFailure && !hasUsablePoints
    ? 'failed'
    : (options.disabled || (response && response.enabled === false)) &&
        !hasUsablePoints
      ? 'disabled'
      : undefined;

  const sides: Array<[LinkTelemetryEndpoint, LinkTelemetrySide | undefined]> = [
    ['a', response?.source],
    ['z', response?.target]
  ];
  const metrics = sides.flatMap(([endpoint, side]) =>
    (side?.metrics ?? []).map((metric, index) =>
      normalizeMetric(
        metric,
        endpoint,
        forcedState
          ? { state: forcedState, diagnostic: responseDiagnostic }
          : availabilityDetail(side?.availability),
        nowMs,
        index,
        retainedDiagnostic
      )
    )
  );

  const buckets = LINK_TELEMETRY_CATEGORIES.map(category => {
    const byEndpoint = (endpoint: LinkTelemetryEndpoint) => {
      const side = endpoint === 'a' ? response?.source : response?.target;
      const fallback = forcedState
        ? { state: forcedState, diagnostic: responseDiagnostic }
        : groupAvailability(side, category) ??
          availabilityDetail(side?.availability);
      return buildEndpoint(
        endpoint,
        metrics.filter(
          metric =>
            metric.category === category && metric.endpoint === endpoint
        ),
        fallback
      );
    };
    const endpoints = { a: byEndpoint('a'), z: byEndpoint('z') };
    const categoryMetrics = [
      ...endpoints.a.metrics,
      ...endpoints.z.metrics
    ];
    const units = Array.from(
      new Set(categoryMetrics.map(metric => metric.unit))
    ).sort();
    const state = bucketState(endpoints.a, endpoints.z);
    return {
      category,
      state,
      endpoints,
      unitGroups: units.map(unit => ({
        unit,
        metrics: categoryMetrics.filter(metric => metric.unit === unit)
      })),
      hasPartialData: state === 'partial'
    };
  });

  const populatedStates = buckets
    .filter(
      bucket =>
        bucket.endpoints.a.metrics.length > 0 ||
        bucket.endpoints.z.metrics.length > 0
    )
    .map(bucket => bucket.state);
  let state: LinkTelemetryViewState;
  if (forcedState) state = forcedState;
  else if (options.loading && !response) state = 'loading';
  else if (!response) state = 'unavailable';
  else if (populatedStates.includes('partial')) state = 'partial';
  else if (populatedStates.includes('available')) state = 'available';
  else if (populatedStates.includes('stale')) state = 'stale';
  else if (populatedStates.includes('failed')) state = 'failed';
  else if (populatedStates.includes('disabled')) state = 'disabled';
  else if (populatedStates.includes('unavailable')) state = 'unavailable';
  else if (populatedStates.includes('unsupported')) state = 'unsupported';
  else state = 'no_samples_in_range';

  return {
    linkId: safeText(response?.link_id, 160) || undefined,
    state,
    serverTime: safeText(response?.server_time, 64) || undefined,
    telemetryUpdatedAt:
      safeText(response?.telemetry_updated_at, 64) || undefined,
    diagnostic: responseDiagnostic ?? retainedDiagnostic,
    buckets
  };
};

export const buildLinkDetailTelemetryRequest = (
  linkId: string,
  rangeSeconds: number,
  realtime: boolean
) => ({
  link: { id: linkId.trim() },
  rangeSeconds,
  realtime
});
